import {
  Component,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RemoteLoadingFrame } from "@workspace/remote-runtime";
import { useAuthStore } from "../../state/auth-store";
import { useRemoteModule } from "../../hooks/useRemoteModule";
import { getRemoteDefinition } from "../../lib/remotes";
import type { HostContext } from "../../types";
import { useShellLocaleStore } from "../../state/shell-locale-store";
import { getShellRuntime } from "../../infrastructure/shell-runtime/runtime";
import { resolveSupportedLocale } from "../../lib/locale-preferences";
import {
  createRemoteHeadManager,
  type RemoteHeadManager,
} from "./remote-head-manager";

interface RemoteRenderBoundaryProps {
  appKey?: string;
  children: ReactNode;
  errorTitle: string;
}

interface RemoteRenderBoundaryState {
  error?: Error;
}

class RemoteRenderBoundary extends Component<
  RemoteRenderBoundaryProps,
  RemoteRenderBoundaryState
> {
  state: RemoteRenderBoundaryState = {};

  static getDerivedStateFromError(error: Error): RemoteRenderBoundaryState {
    return { error };
  }

  componentDidCatch(error: Error) {
    console.error("[RemoteAppHost] Remote render failed", {
      appKey: this.props.appKey,
      error,
    });
  }

  render() {
    if (this.state.error) {
      return (
        <div className="flex h-full min-h-0 w-full items-center justify-center bg-surface-page">
          <div className="max-w-md rounded-lg border border-state-danger/30 bg-surface-primary p-6 text-center shadow-surface">
            <p className="mb-2 text-sm font-medium text-state-danger">
              {this.props.errorTitle}
            </p>
            <p className="text-sm text-content-secondary">
              {this.props.appKey ?? "remote"}: {this.state.error.message}
            </p>
          </div>
        </div>
      );
    }

    return this.props.children ?? null;
  }
}

interface RemoteLoadFallbackProps {
  appKey?: string;
  error?: string;
  onRetry?: () => void;
}

function RemoteLoadFallback({
  appKey,
  error,
  onRetry,
}: RemoteLoadFallbackProps) {
  const { t } = useTranslation("layout");
  return (
    <div className="flex h-full min-h-0 w-full items-center justify-center bg-surface-page">
      <div className="max-w-md rounded-lg border border-state-danger/30 bg-surface-primary p-6 text-center shadow-surface">
        <p className="mb-2 text-sm font-medium text-state-danger">
          {t("remoteHost.loadError")}
        </p>
        <p className="text-sm text-content-secondary">
          {appKey ?? "remote"}: {error ?? t("remoteHost.loadErrorDescription")}
        </p>
        {onRetry ? (
          <button
            type="button"
            className="mt-4 inline-flex rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
            onClick={onRetry}
          >
            {t("common.retry")}
          </button>
        ) : null}
      </div>
    </div>
  );
}

export function RemoteAppHost() {
  const { t } = useTranslation("layout");
  const { appKey } = useParams<{ appKey: string }>();
  const containerRef = useRef<HTMLDivElement>(null);
  const headManagerRef = useRef<RemoteHeadManager | null>(null);
  const { token, user, tenant, apps } = useAuthStore();
  const [retryNonce, setRetryNonce] = useState(0);
  const [isIsolationPrepared, setIsIsolationPrepared] = useState(false);
  const hasCompleteHostSession = Boolean(token && user?.id && tenant?.id);
  const locale = useShellLocaleStore((state) => state.locale);
  const remoteDefinition = getRemoteDefinition(appKey);
  const isolationMode = remoteDefinition?.isolationMode ?? "none";
  const userId = user?.id ?? null;
  const userName = user?.name ?? null;
  const userEmail = user?.email ?? null;
  const userDni = user?.dni ?? null;
  const userRole =
    apps.find((app) => app.key === appKey)?.effectiveRole ?? user?.role;
  const userLocale = user?.locale;
  const tenantId = tenant?.id ?? null;
  const tenantName = tenant?.name ?? null;
  const tenantSlug = tenant?.slug ?? null;
  const tenantSchema = tenant?.schema;
  const effectiveLocale = resolveSupportedLocale(userLocale) ?? locale;

  const hostUser = useMemo(
    () =>
      userId && userName && userEmail
        ? {
            id: userId,
            name: userName,
            email: userEmail,
            dni: userDni,
            role: userRole,
          }
        : undefined,
    [userDni, userEmail, userId, userName, userRole],
  );
  const hostTenant = useMemo(
    () =>
      tenantId && tenantName && tenantSlug
        ? {
            id: tenantId,
            name: tenantName,
            slug: tenantSlug,
            schema: tenantSchema,
          }
        : undefined,
    [tenantId, tenantName, tenantSchema, tenantSlug],
  );

  // Create the host context consumed by federated remotes.
  const hostContext: HostContext = useMemo(
    () => ({
      token,
      routeBase: remoteDefinition?.route,
      locale: effectiveLocale,
      user: hostUser,
      tenant: hostTenant,
    }),
    [effectiveLocale, hostTenant, hostUser, remoteDefinition?.route, token],
  );

  const effectiveRemoteDefinition =
    remoteDefinition && hasCompleteHostSession ? remoteDefinition : undefined;
  const canLoadRemote =
    effectiveRemoteDefinition &&
    (isolationMode !== "head-style-cleanup" || isIsolationPrepared)
      ? effectiveRemoteDefinition
      : undefined;
  const {
    component: Component,
    status,
    error,
  } = useRemoteModule(canLoadRemote, retryNonce);

  useLayoutEffect(() => {
    const remoteKey = effectiveRemoteDefinition?.key;
    if (!remoteKey) {
      headManagerRef.current?.cleanup();
      headManagerRef.current = null;
      setIsIsolationPrepared(false);
      return;
    }

    if (isolationMode !== "head-style-cleanup") {
      headManagerRef.current?.cleanup();
      headManagerRef.current = null;
      setIsIsolationPrepared(true);
      return;
    }

    headManagerRef.current?.cleanup();
    headManagerRef.current = createRemoteHeadManager(remoteKey);
    setIsIsolationPrepared(true);

    return () => {
      headManagerRef.current?.cleanup();
      headManagerRef.current = null;
    };
  }, [effectiveRemoteDefinition?.key, isolationMode, retryNonce]);

  useEffect(() => {
    const remoteKey = effectiveRemoteDefinition?.key;
    if (!remoteKey) {
      return;
    }

    const shellRuntime = getShellRuntime();

    return () => {
      shellRuntime.lifecycle.flushDisposers(remoteKey);
      headManagerRef.current?.cleanup();
      headManagerRef.current = null;
    };
  }, [effectiveRemoteDefinition?.key, retryNonce]);

  if (!hasCompleteHostSession) {
    return <RemoteLoadingFrame label={t("remoteHost.preparingSession")} />;
  }

  if (!canLoadRemote || status === "idle" || status === "loading") {
    return <RemoteLoadingFrame label={t("remoteHost.loading")} />;
  }

  if (status === "error") {
    return (
      <RemoteLoadFallback
        appKey={appKey}
        error={error}
        onRetry={() => setRetryNonce((current) => current + 1)}
      />
    );
  }

  if (!Component) {
    return (
      <div className="flex h-full min-h-0 w-full items-center justify-center bg-surface-page">
        <div className="max-w-md rounded-lg border border-border-subtle bg-surface-primary p-6 text-center shadow-surface">
          <p className="text-sm text-content-secondary">
            {t("remoteHost.unavailable", { app: appKey ?? "remote" })}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef}
      className="flex h-full min-h-0 w-full min-w-0 flex-1 flex-col"
    >
      <RemoteRenderBoundary
        key={`${appKey}:${retryNonce}`}
        appKey={appKey}
        errorTitle={t("remoteHost.renderError")}
      >
        <Component key={`${appKey}:${retryNonce}`} hostContext={hostContext} />
      </RemoteRenderBoundary>
    </div>
  );
}
